import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BarChart3, Building2, CalendarDays, FileText, Globe, Images, LogOut, PhoneCall, ShieldCheck, Type, UserPlus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "./auth";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const analyticsRouteLabels = {
  "/": "Homepage soccorso H24",
  "/soccorso-h24": "Pagina soccorso H24",
  "/viscar-soccorso-stradale-h24": "Home pubblicitaria",
  "/soccorso-stradale-messina": "Soccorso stradale Messina",
  "/carroattrezzi-messina": "Carroattrezzi Messina",
  "/soccorso-autostradale-a18-a20": "Soccorso autostradale A18/A20",
  "/soccorso-mezzi-pesanti-messina": "Soccorso mezzi pesanti",
  "/soccorso-stradale-provincia-di-messina": "Messina e provincia",
  "/carroattrezzi-tremestieri": "Carroattrezzi Tremestieri",
  "/soccorso-stradale-milazzo": "Soccorso stradale Milazzo",
  "/soccorso-stradale-taormina": "Soccorso stradale Taormina",
  "/meccatronica": "Meccatronica",
  "/gommista": "Gommista",
  "/contatti": "Contatti",
};

const formatAnalyticsPathLabel = (path = "/") => {
  if (analyticsRouteLabels[path]) return analyticsRouteLabels[path];
  const normalized = path.replace(/^\//, "").replaceAll("-", " ").trim();
  if (!normalized) return "Homepage";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatSourceLabel = (label = "Diretto") => {
  const normalized = label.toLowerCase();
  if (normalized === "diretto") return "Accesso diretto";
  if (normalized.includes("app.emergent")) return "Preview / test tecnico";
  if (normalized === "viscar.it" || normalized === "www.viscar.it") return "Dominio Viscar";
  return label;
};

const getContactActions = (data) => (data?.phone_clicks || 0) + (data?.whatsapp_clicks || 0) + (data?.contact_requests || 0);

const getContactRate = (data) => {
  const visits = data?.visits || 0;
  if (!visits) return "0%";
  return `${Math.round((getContactActions(data) / visits) * 100)}%`;
};

const formatDecimal = (value) => value.toFixed(1).replace(".", ",");

export const PrivateLoginPage = () => {
  const { login, user, isChecking } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isChecking && user) {
      window.location.href = "/area-riservata";
    }
  }, [isChecking, user]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      await login(form);
      window.location.href = "/area-riservata";
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="private-login-page" className="private-auth-page">
      <div className="private-auth-shell">
        <Card className="private-auth-card">
          <CardHeader>
            <CardTitle data-testid="private-login-title">Area riservata Viscar</CardTitle>
            <CardDescription data-testid="private-login-description">Accesso admin, partner e operatori per gestione sito, documenti e turni.</CardDescription>
          </CardHeader>
          <CardContent>
            <form data-testid="private-login-form" className="private-auth-form" onSubmit={handleSubmit}>
              <label className="private-auth-field">
                <span>Email</span>
                <Input data-testid="private-login-email" type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} />
              </label>
              <label className="private-auth-field">
                <span>Password</span>
                <Input data-testid="private-login-password" type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
              </label>
              {error ? <p data-testid="private-login-error" className="private-auth-error">{error}</p> : null}
              <Button data-testid="private-login-submit" type="submit" className="btn-emergency w-full" disabled={isSubmitting}>{isSubmitting ? "Accesso..." : "Accedi"}</Button>
              <Link data-testid="private-login-back-link" to="/" className="private-auth-back-link">Torna al sito</Link>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

const summaryItems = [
  { key: "site", title: "Gestione sito", description: "Modifica testi, immagini e contenuti operativi del sito.", icon: ShieldCheck, adminOnly: true },
  { key: "analytics", title: "Statistiche sito", description: "Visite, click, provenienza traffico e richieste ricevute.", icon: BarChart3, adminOnly: true },
  { key: "documents", title: "Documenti", description: "Archivio interno per partner e operatori di reparto.", icon: FileText },
  { key: "shifts", title: "Turni di lavoro", description: "Consultazione rapida dei turni e delle assegnazioni operative.", icon: CalendarDays },
];

export const PrivateDashboardPage = () => {
  const { user, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [userForm, setUserForm] = useState({ email: "", password: "", name: "", role: "partner", branch: "meccatronica" });
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [documentForm, setDocumentForm] = useState({ title: "", category: "Documenti", branch: "meccatronica", audience: "partner,operatore", file: null });
  const [shiftForm, setShiftForm] = useState({ title: "", branch: user?.branch || "meccatronica", assigned_role: "operatore", date: "", start_time: "", end_time: "", notes: "" });
  const [editingShiftId, setEditingShiftId] = useState(null);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [isSavingShift, setIsSavingShift] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState("30");
  const [activeTab, setActiveTab] = useState(user?.role === "admin" ? "site" : "documents");

  const loadDashboard = async () => {
    const response = await fetch(`${API_BASE}/api/private/dashboard`, { credentials: "include" });
    if (!response.ok) {
      throw new Error("Non sono riuscito a caricare l'area riservata");
    }
    const data = await response.json();
    setDashboard(data);
    return data;
  };

  const loadAnalytics = async () => {
    if (user?.role !== "admin") return null;
    const response = await fetch(`${API_BASE}/api/private/analytics?days=${analyticsRange}`, { credentials: "include" });
    if (!response.ok) {
      throw new Error("Non sono riuscito a caricare le statistiche del sito");
    }
    const data = await response.json();
    setAnalytics(data);
    return data;
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        await loadDashboard();
        if (user?.role === "admin") {
          await loadAnalytics();
        }
      } catch (loadError) {
        setError(loadError.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, [analyticsRange, user?.role]);

  const documents = dashboard?.documents || [];
  const shifts = dashboard?.shifts || [];
  const branches = dashboard?.partner_branches || [];

  const branchOptions = useMemo(() => branches.length ? branches : [
    { branch: "soccorso-h24", title: "Soccorso H24" },
    { branch: "meccatronica", title: "Meccatronica" },
    { branch: "gommista", title: "Gommista" },
  ], [branches]);

  const visibleSummaryItems = useMemo(
    () => summaryItems.filter((item) => !item.adminOnly || user?.role === "admin"),
    [user?.role],
  );

  const allowedBranchOptions = useMemo(() => {
    if (user?.role === "admin") return branchOptions;
    return branchOptions.filter((option) => option.branch === user?.branch);
  }, [branchOptions, user?.branch, user?.role]);

  const resolvedDocumentBranch = user?.role === "admin" ? documentForm.branch : (user?.branch || documentForm.branch);
  const resolvedShiftBranch = user?.role === "admin" ? shiftForm.branch : (user?.branch || shiftForm.branch);
  const timelineMax = useMemo(() => Math.max(...(analytics?.timeline || []).map((item) => item.page_views + item.phone_clicks + item.whatsapp_clicks + item.contact_requests), 1), [analytics]);
  const analyticsSummary = analytics?.summary || { visits: 0, page_views: 0, phone_clicks: 0, whatsapp_clicks: 0, contact_requests: 0 };
  const contactActions = getContactActions(analyticsSummary);
  const contactRate = getContactRate(analyticsSummary);
  const pagesPerVisit = analyticsSummary.visits ? formatDecimal(analyticsSummary.page_views / analyticsSummary.visits) : "0,0";
  const analyticsOverviewCards = [
    { key: "sessions", value: analyticsSummary.visits, label: "Sessioni", helper: "Visite uniche registrate nel periodo" },
    { key: "views", value: analyticsSummary.page_views, label: "Pagine viste", helper: `${pagesPerVisit} pagine per visita` },
    { key: "phone", value: analyticsSummary.phone_clicks, label: "Click Chiama", helper: "Tap sul numero di soccorso dal sito" },
    { key: "whatsapp", value: analyticsSummary.whatsapp_clicks, label: "Click WhatsApp", helper: "Aperture chat operative da sito" },
    { key: "rate", value: contactRate, label: "Tasso contatto", helper: `${contactActions} azioni di contatto registrate` },
  ];
  const analyticsComparisonCards = [
    { key: "daily", label: "Oggi", data: analytics?.daily },
    { key: "weekly", label: "Ultimi 7 giorni", data: analytics?.weekly },
    { key: "monthly", label: "Ultimi 30 giorni", data: analytics?.monthly },
  ];
  const formattedTopPages = (analytics?.top_pages || []).map((item) => ({
    ...item,
    cleanLabel: formatAnalyticsPathLabel(item.sublabel || "/"),
  }));
  const formattedTopSources = (analytics?.top_sources || []).map((item) => ({
    ...item,
    cleanLabel: formatSourceLabel(item.label),
  }));
  const formattedTopInteractions = (analytics?.top_interactions || []).map((item) => ({
    ...item,
    cleanLabel: item.label === "Richiesta contatto" ? "Richiesta inviata" : item.label,
    cleanType: item.sublabel === "phone_click" ? "Click Chiama" : item.sublabel === "whatsapp_click" ? "Click WhatsApp" : item.sublabel === "contact_request" ? "Richiesta" : item.sublabel,
  }));
  const formattedTopDevices = (analytics?.top_devices || []).map((item) => ({
    ...item,
    cleanLabel: item.label === "mobile" ? "Mobile" : item.label === "desktop" ? "Desktop" : item.label === "tablet" ? "Tablet" : item.label,
  }));
  const formattedTopCampaigns = (analytics?.top_campaigns || []).map((item) => ({
    ...item,
    cleanLabel: item.label || "Campagna non indicata",
  }));

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setIsSavingUser(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/private/users`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userForm),
      });
      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload?.detail || "Creazione utente non riuscita");
      }
      setUserForm({ email: "", password: "", name: "", role: "partner", branch: "meccatronica" });
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleUploadDocument = async (event) => {
    event.preventDefault();
    if (!documentForm.file) return;
    setIsSavingDocument(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("title", documentForm.title);
      formData.append("category", documentForm.category);
      formData.append("branch", resolvedDocumentBranch);
      formData.append("audience", documentForm.audience);
      formData.append("file", documentForm.file);
      const response = await fetch(`${API_BASE}/api/private/documents`, { method: "POST", credentials: "include", body: formData });
      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload?.detail || "Upload documento non riuscito");
      }
      setDocumentForm({ title: "", category: "Documenti", branch: user?.branch || "meccatronica", audience: "partner,operatore", file: null });
      await loadDashboard();
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSavingDocument(false);
    }
  };

  const handleCreateShift = async (event) => {
    event.preventDefault();
    setIsSavingShift(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/private/shifts${editingShiftId ? `/${editingShiftId}` : ""}`, {
        method: editingShiftId ? "PUT" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...shiftForm, branch: resolvedShiftBranch }),
      });
      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload?.detail || "Creazione turno non riuscita");
      }
      setShiftForm({ title: "", branch: user?.branch || "meccatronica", assigned_role: "operatore", date: "", start_time: "", end_time: "", notes: "" });
      setEditingShiftId(null);
      await loadDashboard();
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSavingShift(false);
    }
  };

  const handleDeleteShift = async (shiftId) => {
    await fetch(`${API_BASE}/api/private/shifts/${shiftId}`, { method: "DELETE", credentials: "include" });
    if (editingShiftId === shiftId) {
      setEditingShiftId(null);
      setShiftForm({ title: "", branch: user?.branch || "meccatronica", assigned_role: "operatore", date: "", start_time: "", end_time: "", notes: "" });
    }
    await loadDashboard();
  };

  const shiftsByDate = useMemo(() => shifts.reduce((accumulator, item) => {
    accumulator[item.date] = accumulator[item.date] || [];
    accumulator[item.date].push(item);
    return accumulator;
  }, {}), [shifts]);

  const handleDeleteDocument = async (documentId) => {
    await fetch(`${API_BASE}/api/private/documents/${documentId}`, { method: "DELETE", credentials: "include" });
    await loadDashboard();
  };

  return (
    <main data-testid="private-dashboard-page" className="private-dashboard-page">
      <div className="private-dashboard-shell">
        <header data-testid="private-dashboard-header" className="private-dashboard-header">
          <div>
            <span className="section-eyebrow">Area riservata</span>
            <h1 data-testid="private-dashboard-title">Dashboard {user?.role === "admin" ? "admin" : user?.role}</h1>
            <p data-testid="private-dashboard-description">Accesso riservato per gestione sito, documenti e turni di lavoro dei reparti Viscar.</p>
          </div>
          <div className="private-dashboard-header__actions">
            <div data-testid="private-dashboard-user-chip" className="private-user-chip"><Users size={16} /> {user?.name} · {user?.role}</div>
            <Button data-testid="private-dashboard-logout" className="btn-ghost-tech" onClick={logout}><LogOut size={16} /> Esci</Button>
          </div>
        </header>

        <section data-testid="private-dashboard-summary" className="private-summary-grid">
          {visibleSummaryItems.map((item) => {
            const Icon = item.icon;
            return (
              <Card data-testid={`private-summary-card-${item.key}`} key={item.key} className="private-summary-card">
                <CardHeader>
                  <button
                    data-testid={`private-summary-open-${item.key}`}
                    type="button"
                    className="private-summary-card__button"
                    onClick={() => setActiveTab(item.key)}
                  >
                  <div className="private-summary-card__icon"><Icon size={18} /></div>
                  <CardTitle>{item.title}</CardTitle>
                  <CardDescription>{item.description}</CardDescription>
                  </button>
                </CardHeader>
              </Card>
            );
          })}
        </section>

        {error ? <p data-testid="private-dashboard-error" className="private-auth-error">{error}</p> : null}

        {isLoading ? <div data-testid="private-dashboard-loading" className="private-loading-screen">Caricamento dashboard...</div> : (
          <Tabs data-testid="private-dashboard-tabs" value={activeTab} onValueChange={setActiveTab} className="private-tabs">
            <TabsList className="private-tabs-list">
              {user?.role === "admin" ? <TabsTrigger data-testid="private-tab-site" value="site">Gestione sito</TabsTrigger> : null}
              {user?.role === "admin" ? <TabsTrigger data-testid="private-tab-analytics" value="analytics">Statistiche</TabsTrigger> : null}
              <TabsTrigger data-testid="private-tab-documents" value="documents">Documenti</TabsTrigger>
              <TabsTrigger data-testid="private-tab-shifts" value="shifts">Turni</TabsTrigger>
              <TabsTrigger data-testid="private-tab-branches" value="branches">Reparti</TabsTrigger>
              {user?.role === "admin" ? <TabsTrigger data-testid="private-tab-users" value="users">Utenti</TabsTrigger> : null}
            </TabsList>

            {user?.role === "admin" ? (
              <TabsContent value="site">
                <div data-testid="private-site-management-grid" className="private-card-grid">
                  <Card data-testid="private-site-texts-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><Type size={16} /> Modifica testi</CardTitle>
                      <CardDescription>Apri il sito pubblico mentre sei loggato come admin, poi attiva la modalità modifica testi per salvare i contenuti in modo permanente.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-data-card__actions">
                      <Button data-testid="private-site-open-text-editor" asChild className="btn-emergency">
                        <a href="/" target="_blank" rel="noreferrer"><ArrowUpRight size={16} /> Apri il sito e modifica i testi</a>
                      </Button>
                    </CardContent>
                  </Card>

                  <Card data-testid="private-site-images-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><Images size={16} /> Gestione immagini</CardTitle>
                      <CardDescription>Accedi al pannello dedicato per assegnare, aggiornare e organizzare le immagini operative del sito Viscar.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-data-card__actions">
                      <Button data-testid="private-site-open-photo-management" asChild className="btn-ghost-tech">
                        <Link to="/gestione-foto"><ArrowUpRight size={16} /> Apri gestione immagini</Link>
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            ) : null}

            {user?.role === "admin" ? (
              <TabsContent value="analytics">
                <div data-testid="private-analytics-toolbar" className="private-analytics-toolbar">
                  <div>
                    <span className="section-eyebrow">Monitoraggio sito</span>
                    <h2 data-testid="private-analytics-title">Monitoraggio contatti e traffico del sito</h2>
                    <p data-testid="private-analytics-description">Vista admin orientata a capire quali pagine portano visite, quali canali portano contatti e quanto il sito sta trasformando il traffico in chiamate o messaggi.</p>
                  </div>
                  <div data-testid="private-analytics-range-control" className="private-analytics-range-control">
                    <span>Periodo</span>
                    <select value={analyticsRange} onChange={(event) => setAnalyticsRange(event.target.value)} className="private-native-select" data-testid="private-analytics-range-select">
                      <option value="1">1 giorno</option>
                      <option value="7">7 giorni</option>
                      <option value="30">30 giorni</option>
                    </select>
                  </div>
                </div>

                <Card data-testid="private-analytics-visual-card" className="private-data-card private-analytics-visual-card">
                  <CardHeader>
                    <CardTitle><BarChart3 size={16} /> Quadro rapido del periodo selezionato</CardTitle>
                    <CardDescription>Qui sopra hai i numeri che servono davvero per leggere il rendimento del sito: sessioni, pagine viste e azioni di contatto.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div data-testid="private-analytics-visual-metrics" className="private-analytics-visual-metrics">
                      {analyticsOverviewCards.map((item) => (
                        <div data-testid={`private-analytics-overview-${item.key}`} key={item.key} className="private-analytics-stat-card">
                          <strong>{item.value}</strong>
                          <span>{item.label}</span>
                          <small>{item.helper}</small>
                        </div>
                      ))}
                    </div>
                    <div data-testid="private-analytics-chart" className="private-analytics-chart">
                      {(analytics?.timeline || []).map((item) => {
                        const total = item.page_views + item.phone_clicks + item.whatsapp_clicks + item.contact_requests;
                        const contactTotal = getContactActions(item);
                        const height = Math.max(18, Math.round((total / timelineMax) * 180));
                        return (
                          <div data-testid={`private-analytics-chart-bar-${item.date}`} key={item.date} className="private-analytics-chart__item">
                            <div className="private-analytics-chart__stack">
                              <span className="private-analytics-chart__bar" style={{ height }} />
                            </div>
                            <strong>{total}</strong>
                            <small>{item.date.slice(5)}</small>
                            <small className="private-analytics-chart__meta">Contatti: {contactTotal}</small>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <div data-testid="private-analytics-summary-grid" className="private-analytics-summary-grid">
                  {analyticsComparisonCards.map((item) => (
                    <Card data-testid={`private-analytics-period-${item.key}`} key={item.key} className="private-data-card">
                      <CardHeader>
                        <CardTitle><BarChart3 size={16} /> {item.label}</CardTitle>
                        <CardDescription>Snapshot operativo utile per confrontare velocemente volume e capacità di contatto.</CardDescription>
                      </CardHeader>
                      <CardContent className="private-analytics-kpis">
                        <div data-testid={`private-analytics-${item.key}-visits`}><strong>{item.data?.visits || 0}</strong><span>Sessioni</span></div>
                        <div data-testid={`private-analytics-${item.key}-contacts`}><strong>{getContactActions(item.data)}</strong><span>Azioni di contatto</span></div>
                        <div data-testid={`private-analytics-${item.key}-phone`}><strong>{item.data?.phone_clicks || 0}</strong><span>Click Chiama</span></div>
                        <div data-testid={`private-analytics-${item.key}-whatsapp`}><strong>{item.data?.whatsapp_clicks || 0}</strong><span>Click WhatsApp</span></div>
                        <div data-testid={`private-analytics-${item.key}-rate`}><strong>{getContactRate(item.data)}</strong><span>Tasso contatto</span></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div data-testid="private-analytics-grid" className="private-analytics-grid">
                  <Card data-testid="private-top-pages-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><Globe size={16} /> Pagine più consultate</CardTitle>
                      <CardDescription>Ti mostra dove si concentra davvero l’attenzione del traffico nel periodo scelto.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-analytics-list">
                      {formattedTopPages.length ? formattedTopPages.map((item, index) => <div data-testid={`private-top-page-${index}`} key={`${item.sublabel}-${index}`}><strong>{item.cleanLabel}</strong><span>{item.sublabel}</span><b>{item.value}</b></div>) : <div data-testid="private-top-pages-empty"><strong>Nessun dato nel periodo</strong><span>Le pagine più consultate compariranno qui appena il sito riceve traffico.</span></div>}
                    </CardContent>
                  </Card>

                  <Card data-testid="private-top-sources-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><ArrowUpRight size={16} /> Da dove arrivano i visitatori</CardTitle>
                      <CardDescription>Serve a capire se il sito viene trovato da Google, accessi diretti, referral o canali tecnici di test.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-analytics-list">
                      {formattedTopSources.length ? formattedTopSources.map((item, index) => <div data-testid={`private-top-source-${index}`} key={`${item.label}-${index}`}><strong>{item.cleanLabel}</strong><span>{item.sublabel}</span><b>{item.value}</b></div>) : <div data-testid="private-top-sources-empty"><strong>Nessuna sorgente registrata</strong><span>Appena il traffico cresce, qui vedrai i canali più utili.</span></div>}
                    </CardContent>
                  </Card>

                  <Card data-testid="private-top-interactions-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><PhoneCall size={16} /> Azioni di contatto più usate</CardTitle>
                      <CardDescription>Qui capisci se il sito spinge di più verso chiamata, WhatsApp o richieste inviate.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-analytics-list">
                      {formattedTopInteractions.length ? formattedTopInteractions.map((item, index) => <div data-testid={`private-top-interaction-${index}`} key={`${item.label}-${index}`}><strong>{item.cleanLabel}</strong><span>{item.cleanType}</span><b>{item.value}</b></div>) : <div data-testid="private-top-interactions-empty"><strong>Nessuna azione di contatto</strong><span>Quando partono i primi click Chiama o WhatsApp, li leggerai qui.</span></div>}
                    </CardContent>
                  </Card>

                  <Card data-testid="private-top-devices-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><Building2 size={16} /> Dispositivi usati</CardTitle>
                      <CardDescription>Ti aiuta a capire se i visitatori arrivano soprattutto da mobile, desktop o tablet.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-analytics-list">
                      {formattedTopDevices.length ? formattedTopDevices.map((item, index) => <div data-testid={`private-top-device-${index}`} key={`${item.label}-${index}`}><strong>{item.cleanLabel}</strong><span>{item.sublabel}</span><b>{item.value}</b></div>) : <div data-testid="private-top-devices-empty"><strong>Nessun dato dispositivo</strong><span>Quando il sito riceve traffico utile, qui vedrai da quali dispositivi arrivano le visite.</span></div>}
                    </CardContent>
                  </Card>

                  <Card data-testid="private-top-campaigns-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><ArrowUpRight size={16} /> Campagne e link tracciati</CardTitle>
                      <CardDescription>Qui compariranno le visite con UTM, così puoi distinguere meglio social, contenuti e campagne quando i link sono tracciati.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-analytics-list">
                      {formattedTopCampaigns.length ? formattedTopCampaigns.map((item, index) => <div data-testid={`private-top-campaign-${index}`} key={`${item.cleanLabel}-${index}`}><strong>{item.cleanLabel}</strong><span>{item.sublabel}</span><b>{item.value}</b></div>) : <div data-testid="private-top-campaigns-empty"><strong>Nessun link tracciato nel periodo</strong><span>Quando arrivano visite con parametri UTM, qui vedrai campagna, sorgente e contenuto.</span></div>}
                    </CardContent>
                  </Card>

                  <Card data-testid="private-recent-requests-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><Users size={16} /> Contatti arrivati dal sito</CardTitle>
                      <CardDescription>Ultime richieste inviate dal form o dagli strumenti di contatto registrati come richiesta.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-requests-list">
                      {(analytics?.recent_requests || []).length ? (analytics?.recent_requests || []).map((item) => <div data-testid={`private-recent-request-${item.id}`} key={item.id}><strong>{item.name || "Contatto senza nome"}</strong><span>{item.problem || item.submitted_via}</span><p>{item.position || formatAnalyticsPathLabel(item.page_path)}</p></div>) : <div data-testid="private-recent-requests-empty"><strong>Nessuna richiesta nel periodo</strong><span>Le richieste vere ricevute dal sito compariranno qui con problema segnalato e posizione.</span></div>}
                    </CardContent>
                  </Card>
                </div>

                <div data-testid="private-integrations-grid" className="private-card-grid private-card-grid--single">
                  <Card data-testid="private-integrations-status-card" className="private-data-card">
                    <CardHeader>
                      <CardTitle><ShieldCheck size={16} /> Stato strumenti</CardTitle>
                      <CardDescription>Riepilogo essenziale degli strumenti già collegati al sito, utile solo come check tecnico rapido admin.</CardDescription>
                    </CardHeader>
                    <CardContent className="private-integrations-list">
                      {(analytics?.integrations || []).map((item, index) => <div data-testid={`private-integration-${index}`} key={item.title}><strong>{item.title}</strong><span>{item.status}</span><p>{item.description}</p></div>)}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            ) : null}

            <TabsContent value="documents">
              {(user?.role === "admin" || user?.role === "partner") ? (
                <Card data-testid="private-upload-document-card" className="private-data-card">
                  <CardHeader>
                    <CardTitle>Carica documento</CardTitle>
                    <CardDescription>Upload reale di documenti condivisi con partner e operatori.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form data-testid="private-upload-document-form" className="private-auth-form private-auth-form--grid" onSubmit={handleUploadDocument}>
                      <Input data-testid="private-document-title" placeholder="Titolo documento" value={documentForm.title} onChange={(event) => setDocumentForm((current) => ({ ...current, title: event.target.value }))} />
                      <Input data-testid="private-document-category" placeholder="Categoria" value={documentForm.category} onChange={(event) => setDocumentForm((current) => ({ ...current, category: event.target.value }))} />
                      <select data-testid="private-document-branch" className="private-native-select" value={resolvedDocumentBranch} onChange={(event) => setDocumentForm((current) => ({ ...current, branch: event.target.value }))}>
                        {allowedBranchOptions.map((option) => <option key={option.branch} value={option.branch}>{option.title}</option>)}
                      </select>
                      <Input data-testid="private-document-audience" placeholder="Audience es: partner,operatore" value={documentForm.audience} onChange={(event) => setDocumentForm((current) => ({ ...current, audience: event.target.value }))} />
                      <Input data-testid="private-document-file" type="file" onChange={(event) => setDocumentForm((current) => ({ ...current, file: event.target.files?.[0] || null }))} />
                      <Button data-testid="private-document-submit" type="submit" className="btn-emergency" disabled={isSavingDocument}>{isSavingDocument ? "Caricamento..." : "Carica documento"}</Button>
                    </form>
                  </CardContent>
                </Card>
              ) : null}
              <div data-testid="private-documents-grid" className="private-card-grid">
                {documents.map((item) => (
                  <Card data-testid={`private-document-${item.id}`} key={item.id} className="private-data-card">
                    <CardHeader>
                      <CardTitle>{item.title}</CardTitle>
                      <CardDescription>{item.category} · {item.branch} · {item.filename}</CardDescription>
                    </CardHeader>
                    <CardContent className="private-data-card__actions">
                      <Button data-testid={`private-document-download-${item.id}`} asChild className="btn-ghost-tech"><a href={`${API_BASE}/api/private/documents/${item.id}/download`} target="_blank" rel="noreferrer">Download</a></Button>
                      {user?.role === "admin" ? <Button data-testid={`private-document-delete-${item.id}`} className="btn-emergency" onClick={() => handleDeleteDocument(item.id)}>Elimina</Button> : null}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="shifts">
              {(user?.role === "admin" || user?.role === "partner") ? (
                <Card data-testid="private-create-shift-card" className="private-data-card">
                  <CardHeader>
                    <CardTitle>{editingShiftId ? "Modifica turno" : "Crea turno"}</CardTitle>
                    <CardDescription>Gestione reale dei turni di lavoro per i reparti.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form data-testid="private-create-shift-form" className="private-auth-form private-auth-form--grid" onSubmit={handleCreateShift}>
                      <Input data-testid="private-shift-title" placeholder="Titolo turno" value={shiftForm.title} onChange={(event) => setShiftForm((current) => ({ ...current, title: event.target.value }))} />
                      <select data-testid="private-shift-branch" className="private-native-select" value={resolvedShiftBranch} onChange={(event) => setShiftForm((current) => ({ ...current, branch: event.target.value }))}>
                        {allowedBranchOptions.map((option) => <option key={option.branch} value={option.branch}>{option.title}</option>)}
                      </select>
                      <select data-testid="private-shift-role" className="private-native-select" value={shiftForm.assigned_role} onChange={(event) => setShiftForm((current) => ({ ...current, assigned_role: event.target.value }))}>
                        <option value="operatore">Operatore</option>
                        <option value="partner">Partner</option>
                        <option value="admin">Admin</option>
                      </select>
                      <Input data-testid="private-shift-date" type="date" value={shiftForm.date} onChange={(event) => setShiftForm((current) => ({ ...current, date: event.target.value }))} />
                      <Input data-testid="private-shift-start" type="time" value={shiftForm.start_time} onChange={(event) => setShiftForm((current) => ({ ...current, start_time: event.target.value }))} />
                      <Input data-testid="private-shift-end" type="time" value={shiftForm.end_time} onChange={(event) => setShiftForm((current) => ({ ...current, end_time: event.target.value }))} />
                      <Input data-testid="private-shift-notes" placeholder="Note" value={shiftForm.notes} onChange={(event) => setShiftForm((current) => ({ ...current, notes: event.target.value }))} />
                      <Button data-testid="private-shift-submit" type="submit" className="btn-emergency" disabled={isSavingShift}>{isSavingShift ? "Salvataggio..." : editingShiftId ? "Aggiorna turno" : "Crea turno"}</Button>
                    </form>
                  </CardContent>
                </Card>
              ) : null}
              <div data-testid="private-shifts-grid" className="private-calendar-grid">
                {Object.entries(shiftsByDate).sort(([left], [right]) => left.localeCompare(right)).map(([date, dayShifts]) => (
                  <Card data-testid={`private-shift-day-${date}`} key={date} className="private-data-card">
                    <CardHeader>
                      <CardTitle>{date}</CardTitle>
                      <CardDescription>Calendario reparto</CardDescription>
                    </CardHeader>
                    <CardContent className="private-day-shifts">
                      {dayShifts.map((item) => (
                        <div data-testid={`private-shift-${item.id}`} key={item.id} className="private-shift-item">
                          <strong>{item.title}</strong>
                          <span>{item.start_time} - {item.end_time} · {item.branch}</span>
                          {item.notes ? <p className="private-shift-notes">{item.notes}</p> : null}
                          {(user?.role === "admin" || user?.role === "partner") ? (
                            <div className="private-data-card__actions">
                              <Button data-testid={`private-shift-edit-${item.id}`} className="btn-ghost-tech" onClick={() => { setEditingShiftId(item.id); setShiftForm({ title: item.title, branch: item.branch, assigned_role: item.assigned_role, date: item.date, start_time: item.start_time, end_time: item.end_time, notes: item.notes || "" }); }}>Modifica</Button>
                              <Button data-testid={`private-shift-delete-${item.id}`} className="btn-emergency" onClick={() => handleDeleteShift(item.id)}>Elimina</Button>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="branches">
              <div data-testid="private-branches-grid" className="private-card-grid">
                {branches.map((item) => (
                  <Card data-testid={`private-branch-${item.branch}`} key={item.branch} className="private-data-card">
                    <CardHeader>
                      <CardTitle><Building2 size={16} /> {item.title}</CardTitle>
                      <CardDescription>{item.summary}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {user?.role === "admin" ? (
              <TabsContent value="users">
                <Card data-testid="private-create-user-card" className="private-data-card">
                  <CardHeader>
                    <CardTitle><UserPlus size={16} /> Nuovo utente</CardTitle>
                    <CardDescription>Crea accessi per partner e operatori dei diversi reparti.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form data-testid="private-create-user-form" className="private-auth-form private-auth-form--grid" onSubmit={handleCreateUser}>
                      <Input data-testid="private-create-user-name" placeholder="Nome" value={userForm.name} onChange={(event) => setUserForm((current) => ({ ...current, name: event.target.value }))} />
                      <Input data-testid="private-create-user-email" placeholder="Email" type="email" value={userForm.email} onChange={(event) => setUserForm((current) => ({ ...current, email: event.target.value }))} />
                      <Input data-testid="private-create-user-password" placeholder="Password" type="password" value={userForm.password} onChange={(event) => setUserForm((current) => ({ ...current, password: event.target.value }))} />
                      <select data-testid="private-create-user-role" className="private-native-select" value={userForm.role} onChange={(event) => setUserForm((current) => ({ ...current, role: event.target.value }))}>
                        <option value="partner">Partner</option>
                        <option value="operatore">Operatore</option>
                        <option value="admin">Admin</option>
                      </select>
                      <select data-testid="private-create-user-branch" className="private-native-select" value={userForm.branch} onChange={(event) => setUserForm((current) => ({ ...current, branch: event.target.value }))}>
                        {branchOptions.map((option) => <option key={option.branch} value={option.branch}>{option.title}</option>)}
                      </select>
                      <Button data-testid="private-create-user-submit" type="submit" className="btn-emergency" disabled={isSavingUser}>{isSavingUser ? "Creazione..." : "Crea utente"}</Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            ) : null}
          </Tabs>
        )}
      </div>
    </main>
  );
};